'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import { DirectionalArrow } from './directional-arrow';
import { Eyebrow } from './eyebrow';

/**
 * Universal section header. Eyebrow + display serif title + optional
 * uppercase action link. Every native section uses this for consistent
 * editorial rhythm — no section should hand-roll a heading.
 */
export function SectionHeader({
  eyebrow,
  title,
  action,
  className,
}: {
  /** Mono micro-label above the title (e.g. "This week"). Optional. */
  eyebrow?: ReactNode;
  /** Display heading. Renders in t.fontDisplay. */
  title: ReactNode;
  /** Optional right-aligned link. */
  action?: { label: ReactNode; href: string };
  className?: string;
}) {
  const t = useNativeTokens();

  return (
    <div
      className={className ?? 'flex items-baseline justify-between px-4 sm:px-5'}
      style={{ paddingTop: t.density === 'spacious' ? 8 : 0 }}
    >
      <div>
        {eyebrow && <Eyebrow style={{ marginBottom: 6 }}>{eyebrow}</Eyebrow>}
        <h2
          style={{
            fontFamily: t.fontDisplay,
            fontSize: t.density === 'spacious' ? 24 : 20,
            fontWeight: t.density === 'spacious' ? 300 : 600,
            color: t.foreground,
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>

      {action && (
        <Link
          href={action.href}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: t.fontSans,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: t.foreground,
            textDecoration: 'none',
          }}
        >
          {action.label}
          <DirectionalArrow size={11} strokeWidth={1.4} />
        </Link>
      )}
    </div>
  );
}
